import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  connectStorageEmulator,
  getStorage,
  type FirebaseStorage,
} from "firebase/storage";
import {
  collection,
  connectFirestoreEmulator,
  getDocs,
  getFirestore,
  query,
  where,
  type Firestore,
} from "firebase/firestore";
import {
  connectAuthEmulator,
  getAuth,
  type Auth,
  type UserCredential,
} from "firebase/auth";

import { authActions } from "../auth/authState";
import { documentActions } from "../documents/documentState";
import {
  FIRESTORE_USER_ROLE_COLLECTION,
  REG_DETAILS_KEY,
  getFirebaseConfig,
} from "../config";
import getApi, {
  setLogRocketId,
  type FirebaseApi,
  type FirebaseUser,
} from "./api";

type Optional<T> = T | undefined;

interface FirebaseContextValue {
  api: FirebaseApi;
  app: FirebaseApp;
  auth: Auth;
  database: Firestore;
  storage: FirebaseStorage;
}

export const FirebaseContext =
  createContext<Optional<FirebaseContextValue>>(undefined);

export function useFirebaseContext() {
  const ctx = useContext(FirebaseContext);
  if (!ctx) {
    throw new Error("<FirebaseContext.Provider /> required");
  }
  return ctx;
}

/**
 * Watch authentication status and appropriately dispatch to redux state.
 */
function useFirebaseAuthObserver(firebase: FirebaseContextValue) {
  const dispatch = useDispatch();
  useEffect(() => {
    const unsubscribe = firebase.auth.onIdTokenChanged(async (user) => {
      if (!user) {
        dispatch(authActions.clearLogin());
        dispatch(documentActions.closeReview());
        return;
      }

      // Otherwise register & dispatch global user state
      setLogRocketId(user as FirebaseUser);
      const snapshot = await getDocs(
        query(
          collection(firebase.database, FIRESTORE_USER_ROLE_COLLECTION),
          where("userId", "==", user.uid)
        )
      );
      const isAdmin =
        snapshot.size === 1 ? snapshot.docs[0].data()?.role === "ADMIN" : false;

      const { uid, email } = user;
      dispatch(
        authActions.setCurrentUser({
          uid,
          email,
          isAdmin,
        })
      );
    });

    return unsubscribe;
  }, [firebase.auth, firebase.database, dispatch]);
}

interface Props {
  children?: React.ReactNode;
}

export default function FirebaseContextProvider({ children }: Props) {
  const [app] = useState(() => initializeApp(getFirebaseConfig()));
  const firebase = useMemo(() => {
    const api = getApi(app);
    const auth = getAuth(app);
    const database = getFirestore(app);
    const storage = getStorage(app);

    if (
      process.env.NODE_ENV === "development" &&
      process.env.REACT_APP_FIREBASE_EMULATE === "1"
    ) {
      console.debug(
        "!!!! LOCAL DEVELOPMENT ENVIRONMENT DETECTED !!! Configuring usage against emulators..."
      );
      connectAuthEmulator(auth, "http://localhost:9099");
      connectFirestoreEmulator(database, "localhost", 8080);
      connectStorageEmulator(storage, "localhost", 9199);
    }

    return { app, api, auth, database, storage };
  }, [app]);
  useFirebaseAuthObserver(firebase);
  return (
    <FirebaseContext.Provider value={firebase}>
      {children}
    </FirebaseContext.Provider>
  );
}

/**
 * This is a little ridiculous, but set this info in session storage
 * to be later retrieved during application document initialization.
 */
export function setUserInSessionStorage(result: UserCredential) {
  const { email, displayName } = result.user;
  const [firstName, lastName] = displayName?.split(" ") ?? [];
  sessionStorage.setItem(
    REG_DETAILS_KEY,
    JSON.stringify({
      firstName,
      lastName,
      email,
    })
  );
}
